// src/components/ui/Skeleton.jsx
import React from "react";

/**
 * Reusable skeleton loader component
 */
export function Skeleton({ className = "", width, height, rounded = "md" }) {
  return (
    <div
      className={`bg-slate-200 animate-pulse ${className}`}
      style={{
        width: width || "100%",
        height: height || "1rem",
        borderRadius: rounded === "full" ? "9999px" : rounded === "lg" ? "0.5rem" : rounded === "xl" ? "0.75rem" : "0.25rem",
      }}
    />
  );
}

/**
 * Table row skeleton
 */
export function TableRowSkeleton({ cols = 5 }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, idx) => (
        <td key={idx} className="px-4 py-3">
          <Skeleton height="1.25rem" />
        </td>
      ))}
    </tr>
  );
}

/**
 * Card skeleton
 */
export function CardSkeleton({ lines = 3 }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="space-y-3">
        <Skeleton height="1.5rem" width="60%" />
        {Array.from({ length: lines }).map((_, idx) => (
          <Skeleton key={idx} height="1rem" width={idx === lines - 1 ? "40%" : "100%"} />
        ))}
      </div>
    </div>
  );
}

/**
 * KPI Card skeleton
 */
export function KpiCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-4">
        <Skeleton width="3rem" height="3rem" rounded="xl" />
        <div className="flex-1 space-y-2">
          <Skeleton height="0.875rem" width="40%" />
          <Skeleton height="1.5rem" width="60%" />
          <Skeleton height="0.75rem" width="30%" />
        </div>
      </div>
    </div>
  );
}

/**
 * List item skeleton
 */
export function ListItemSkeleton() {
  return (
    <div className="flex items-center gap-3 p-3 border-b border-slate-100 last:border-0">
      <Skeleton width="2.5rem" height="2.5rem" rounded="full" />
      <div className="flex-1 space-y-2">
        <Skeleton height="1rem" width="60%" />
        <Skeleton height="0.75rem" width="40%" />
      </div>
      <Skeleton width="4rem" height="1.5rem" rounded="lg" />
    </div>
  );
}

/**
 * Table skeleton with multiple rows
 */
export function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <TableRowSkeleton key={rowIdx} cols={cols} />
      ))}
    </>
  );
}

/**
 * Page skeleton - full page loading state
 */
export function PageSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <Skeleton height="2rem" width="40%" className="mb-2" />
        <Skeleton height="1rem" width="60%" />
      </div>
      
      {/* KPI cards skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>
      
      {/* Content skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 2 }).map((_, i) => (
          <CardSkeleton key={i} lines={4} />
        ))}
      </div>
    </div>
  );
}
